import React, { useEffect, useState, useRef, createElement } from "react";
import { createRoot } from "react-dom/client";
import { CircularProgress } from "@mui/material";
import Modalui from "../modal/Modalui";
import { downloadReport, getReportById } from "src/network/graphql/analyticsService";
import { immediateToast } from "izitoast-react";
import styles from "./reportStatusPopup.module.scss";
import { EMPTY_STRING } from "src/components/constant/constant";
export interface ReportStatusPopupOptions {
  reportId: string;
  fileName?: string;
  customClassName?: string;
  customModalOver?: string;
  customShadowOver?: string;
  backgroundColor?: string;
  onDownload?: (_reportId: string) => void;
  onReportComplete?: (_reportId: string) => void;
  onClose?: () => void;
  onNavigateToReports?: () => void;
}

export interface ReportStatus {
  report_id: string;
  report_type: string;
  status: "PENDING" | "PROCESSING" | "COMPLETED" | "FAILED" | "CREATED";
  report_date: string;
}

interface PopupInstance {
  id: string;
  root: ReturnType<typeof createRoot>;
  container: HTMLDivElement;
  options: ReportStatusPopupOptions;
  isOpen: boolean;
}

const ReportStatusPopupComponent: React.FC<ReportStatusPopupOptions & { isOpen: boolean }> = ({
  isOpen,
  reportId,
  customClassName = EMPTY_STRING,
  customModalOver = EMPTY_STRING,
  customShadowOver = EMPTY_STRING,
  onDownload,
  onReportComplete,
  onClose,
  onNavigateToReports
}) => {
  const [reportStatus, setReportStatus] = useState<ReportStatus | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const checkReportStatus = async () => {
      if (!reportId || !isOpen) return;

      setIsLoading(true);
      setError(null);

      try {
        const response = await getReportById(reportId);

        const report = response.data.get_report;

        if (report) {
          setReportStatus(report);

          if (report.status === "COMPLETED" || report.status === "FAILED") {
            if (intervalRef.current) {
              clearInterval(intervalRef.current);
              intervalRef.current = null;
            }
            if (report.status === "COMPLETED" && onReportComplete) {
              onReportComplete(reportId);
            }
          }
        } else {
          setError("Report not found");
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
        }
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : "Failed to check report status");
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      } finally {
        setIsLoading(false);
      }
    };

    if (isOpen && reportId) {
      checkReportStatus();

      const id = setInterval(() => {
        checkReportStatus();
      }, 5000);

      intervalRef.current = id;

      return () => {
        if (id) {
          clearInterval(id);
          intervalRef.current = null;
        }
      };
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }
  }, [isOpen, reportId, onReportComplete]);

  const handleDownload = async () => {
    if (!reportId) return;

    try {
      setIsLoading(true);
      const response = await downloadReport({ report_id: reportId });

      const downloadUrl = response.data.get_report_download_url.download_url;

      const link = document.createElement("a");

      link.href = downloadUrl;
      link.click();

      immediateToast("success", {
        message: "Report downloaded successfully",
        timeout: 3000,
        position: "topCenter"
      });
      if (onDownload) {
        onDownload(reportId);
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to download report");
    } finally {
      setIsLoading(false);
    }
  };

  const handleReportsPageClick = () => {
    if (onNavigateToReports) {
      onNavigateToReports();
    } else {
      window.open("/reports", "_blank");
    }
    onClose?.();
  };

  const getStatusMessage = () => {
    if (error) return "Report not found";
    if (!reportStatus) return "Initializing report...";
    // The report name and id are deliberately not shown — the message is about
    // the request, not the file. It still tracks the status, so the popup never
    // claims "completed" while the report is still being generated.
    if (reportStatus.status === "FAILED") return "We could not complete your request";
    if (reportStatus.status === "COMPLETED") return "We have completed your request";

    return "We are working on your request";
  };

  const getStatusColor = () => {
    if (error) return styles.statusFailed;
    if (!reportStatus) return styles.statusPending;
    switch (reportStatus.status) {
      case "PENDING":
        return styles.statusPending;
      case "PROCESSING":
        return styles.statusProcessing;
      case "COMPLETED":
        return styles.statusCompleted;
      case "FAILED":
        return styles.statusFailed;
      default:
        return styles.statusPending;
    }
  };

  const isCompleted = reportStatus?.status === "COMPLETED";
  const isFailed = reportStatus?.status === "FAILED" || error;

  return (
    <Modalui
      open={isOpen}
      handleClose={onClose || (() => {})}
      modaluiOver={`${styles.modalContent} ${customModalOver}`}
      modalShadowOver={`${styles.modalShadow} ${customShadowOver}`}
    >
      <div className={`${styles.container} ${customClassName}`}>
        <div className={styles.header}>
          <h3 className={styles.title}>Report Generation</h3>
          <button className={styles.closeButton} onClick={onClose} aria-label="Close popup">
            ×
          </button>
        </div>

        <div className={styles.content}>
          <div className={styles.statusSection}>
            <div className={styles.iconContainer}>
              {isLoading ? (
                <CircularProgress size={40} sx={{ color: "#000000" }} />
              ) : (
                <div className={`${styles.statusIcon} ${getStatusColor()}`}>
                  {isCompleted && !error && "✓"}
                  {isFailed && "✗"}
                  {!isCompleted && !isFailed && (
                    <iframe
                      title="hourglass"
                      className={styles.hourglass}
                      src="https://lottie.host/embed/fc7ae315-7962-4616-bd91-6fa7ece1fb0a/tD9PZDDR0S.lottie"
                    ></iframe>
                  )}
                </div>
              )}
            </div>

            <div className={styles.statusText}>
              <p className={styles.statusMessage}>{getStatusMessage()}</p>
              {error && <p className={styles.errorMessage}>{error}</p>}
            </div>
          </div>

          <div className={styles.infoSection}>
            <p className={styles.infoText}>
              The report is being generated. Once it’s ready, you can download it here or from the{" "}
              <button className={styles.reportsLink} onClick={handleReportsPageClick}>
                Reports
              </button>{" "}
              page.
            </p>
          </div>

          {isCompleted && (
            <div className={styles.downloadSection}>
              <button className={styles.downloadButton} onClick={handleDownload}>
                Download Report
              </button>
            </div>
          )}

          {isFailed && (
            <div className={styles.errorSection}>
              <p className={styles.errorText}>Report generation failed. Please try again or contact support.</p>
            </div>
          )}
        </div>
      </div>
    </Modalui>
  );
};

class ReportStatusPopupManager {
  private instances: Map<string, PopupInstance> = new Map();
  private container: HTMLDivElement | null = null;

  private createContainer(): HTMLDivElement {
    if (this.container) return this.container;

    const container = document.createElement("div");

    container.id = "report-status-popup-container";
    container.style.position = "fixed";
    container.style.top = "0";
    container.style.left = "0";
    container.style.width = "100%";
    container.style.height = "100%";
    container.style.zIndex = "999999";
    container.style.pointerEvents = "none";
    document.body.appendChild(container);

    this.container = container;
    return container;
  }

  private generateId(): string {
    return `report-popup-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  private createPopupInstance(options: ReportStatusPopupOptions): string {
    const id = this.generateId();
    const container = this.createContainer();

    const popupWrapper = document.createElement("div");

    popupWrapper.style.pointerEvents = "auto";
    popupWrapper.id = id;
    container.appendChild(popupWrapper);

    const root = createRoot(popupWrapper);

    const instance: PopupInstance = {
      id,
      root,
      container: popupWrapper,
      options,
      isOpen: true
    };

    this.instances.set(id, instance);
    this.renderPopup(instance);
    return id;
  }

  private renderPopup(instance: PopupInstance): void {
    const { root, options } = instance;

    root.render(
      createElement(ReportStatusPopupComponent, {
        isOpen: instance.isOpen,
        ...options,
        onClose: () => {
          this.close(instance.id);
          if (options.onClose) {
            options.onClose();
          }
        }
      })
    );
  }

  public show(options: ReportStatusPopupOptions): string {
    return this.createPopupInstance(options);
  }

  public close(id: string): boolean {
    const instance = this.instances.get(id);

    if (!instance) return false;

    instance.isOpen = false;
    this.renderPopup(instance);

    setTimeout(() => {
      instance.root.unmount();
      instance.container.remove();
      this.instances.delete(id);

      if (this.instances.size === 0 && this.container) {
        this.container.remove();
        this.container = null;
      }
    }, 300);

    return true;
  }

  public closeAll(): void {
    this.instances.forEach((_, id) => {
      this.close(id);
    });
  }

  public isOpen(id?: string): boolean {
    if (id) {
      const instance = this.instances.get(id);

      return instance ? instance.isOpen : false;
    }
    return this.instances.size > 0;
  }
}

const popupManager = new ReportStatusPopupManager();

export const showReportStatusPopup = (options: ReportStatusPopupOptions): string => {
  return popupManager.show(options);
};

export const closeReportStatusPopup = (id: string): boolean => {
  return popupManager.close(id);
};

export const closeAllReportStatusPopups = (): void => {
  popupManager.closeAll();
};

export const isReportStatusPopupOpen = (id?: string): boolean => {
  return popupManager.isOpen(id);
};

export const showWalletReportPopup = (reportId: string, fileName?: string) => {
  return showReportStatusPopup({
    reportId,
    fileName: fileName || "wallet",
    onNavigateToReports: () => (window.location.href = "/reports")
  });
};

export const showTransactionReportPopup = (reportId: string, fileName?: string) => {
  return showReportStatusPopup({
    reportId,
    fileName: fileName || "transaction",
    onNavigateToReports: () => (window.location.href = "/reports")
  });
};

export default showReportStatusPopup;
